/**
 * Client-Side Rate Limiting Utility
 * 
 * Prevents API spam and protects backend from excessive requests.
 * 
 * Features:
 * - Token bucket algorithm
 * - Sliding window rate limiting
 * - Per-endpoint configuration
 * - Debounce and throttle utilities
 * - TypeScript type safety
 * 
 * Usage:
 * ```ts
 * import { createRateLimiter, debounce, throttle } from '@/lib/rate-limiter';
 * 
 * const limiter = createRateLimiter({ maxRequests: 10, windowMs: 60000 });
 * const debouncedFn = debounce(myFunction, 300);
 * const throttledFn = throttle(myFunction, 1000);
 * ```
 */

/**
 * Rate limiter configuration
 */
export interface RateLimiterConfig {
  /** Maximum number of requests allowed in the time window */
  maxRequests: number;
  /** Time window in milliseconds */
  windowMs: number;
  /** Optional identifier for the limiter (for logging) */
  id?: string;
}

/**
 * Request timestamp tracker
 */
interface RequestTracker {
  timestamps: number[];
  lastCleanup: number;
}

/**
 * Token bucket configuration
 */
export interface TokenBucketConfig {
  /** Maximum tokens in the bucket */
  capacity: number;
  /** Token refill rate (tokens per second) */
  refillRate: number;
  /** Initial token count */
  initialTokens?: number;
}

/**
 * Rate limiter result
 */
export interface RateLimitResult {
  /** Whether the request is allowed */
  allowed: boolean;
  /** Remaining requests in current window */
  remaining: number;
  /** Time until window resets (ms) */
  resetIn: number;
  /** Retry after duration (ms) if not allowed */
  retryAfter?: number;
}

/**
 * Create a sliding window rate limiter
 * 
 * @param config - Rate limiter configuration
 * @returns Rate limiter instance
 * 
 * @example
 * ```ts
 * const limiter = createRateLimiter({
 *   maxRequests: 10,
 *   windowMs: 60000, // 1 minute
 *   id: 'api-search'
 * });
 * 
 * if (limiter.check().allowed) {
 *   await makeApiCall();
 * } else {
 *   toast.error('Too many requests. Please wait.');
 * }
 * ```
 */
export function createRateLimiter(config: RateLimiterConfig) {
  const { maxRequests, windowMs, id = 'default' } = config;
  const tracker: RequestTracker = {
    timestamps: [],
    lastCleanup: Date.now()
  };

  /**
   * Clean up old timestamps outside the current window
   */
  function cleanup() {
    const now = Date.now();
    const cutoff = now - windowMs;
    tracker.timestamps = tracker.timestamps.filter(ts => ts > cutoff);
    tracker.lastCleanup = now;
  }

  /**
   * Check if a request is allowed
   */
  function check(): RateLimitResult {
    cleanup();
    
    const now = Date.now();
    const allowed = tracker.timestamps.length < maxRequests;
    const remaining = Math.max(0, maxRequests - tracker.timestamps.length);
    
    // Calculate reset time
    const oldestTimestamp = tracker.timestamps[0] || now;
    const resetIn = Math.max(0, windowMs - (now - oldestTimestamp));
    
    const result: RateLimitResult = {
      allowed,
      remaining,
      resetIn
    };

    if (!allowed) {
      result.retryAfter = resetIn;
    }

    return result;
  }

  /**
   * Record a request (should be called after check() returns allowed: true)
   */
  function record() {
    tracker.timestamps.push(Date.now());
  }

  /**
   * Attempt a request with rate limiting
   */
  async function attempt<T>(fn: () => Promise<T>): Promise<T> {
    const result = check();
    
    if (!result.allowed) {
      throw new Error(
        `Rate limit exceeded. Please wait ${Math.ceil((result.retryAfter || 0) / 1000)} seconds.`
      );
    }

    record();
    return fn();
  }

  /**
   * Reset the rate limiter
   */
  function reset() {
    tracker.timestamps = [];
    tracker.lastCleanup = Date.now();
  }

  /**
   * Get current status
   */
  function getStatus() {
    cleanup();
    return {
      requestCount: tracker.timestamps.length,
      remaining: maxRequests - tracker.timestamps.length,
      maxRequests,
      windowMs,
      id
    };
  }

  return {
    check,
    record,
    attempt,
    reset,
    getStatus
  };
}

/**
 * Create a token bucket rate limiter
 * 
 * @param config - Token bucket configuration
 * @returns Token bucket instance
 * 
 * @example
 * ```ts
 * const bucket = createTokenBucket({
 *   capacity: 10,
 *   refillRate: 1, // 1 token per second
 *   initialTokens: 10
 * });
 * 
 * if (bucket.tryConsume(1)) {
 *   await makeApiCall();
 * }
 * ```
 */
export function createTokenBucket(config: TokenBucketConfig) {
  const { capacity, refillRate, initialTokens = capacity } = config;
  
  let tokens = initialTokens;
  let lastRefill = Date.now();

  /**
   * Refill tokens based on elapsed time
   */
  function refill() {
    const now = Date.now();
    const elapsed = (now - lastRefill) / 1000; // Convert to seconds
    const tokensToAdd = elapsed * refillRate;
    
    tokens = Math.min(capacity, tokens + tokensToAdd);
    lastRefill = now;
  }

  /**
   * Try to consume tokens
   */
  function tryConsume(count = 1): boolean {
    refill();
    
    if (tokens >= count) {
      tokens -= count;
      return true;
    }
    
    return false;
  }

  /**
   * Get current token count
   */
  function getTokens(): number {
    refill();
    return tokens;
  }

  /**
   * Reset the bucket
   */
  function reset() {
    tokens = initialTokens;
    lastRefill = Date.now();
  }

  return {
    tryConsume,
    getTokens,
    reset
  };
}

/**
 * Debounce a function - delays execution until after wait time has elapsed
 * since the last call
 * 
 * @param fn - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 * 
 * @example
 * ```ts
 * const searchApi = debounce(async (query: string) => {
 *   const results = await fetch(`/api/search?q=${query}`);
 *   return results.json();
 * }, 300);
 * 
 * // Will only execute once, 300ms after last keystroke
 * searchApi('hello');
 * ```
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function debounced(...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      fn(...args);
    }, wait);
  };
}

/**
 * Debounce with promise support - returns a promise that resolves with the function result
 * 
 * @param fn - Async function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function that returns a promise
 * 
 * @example
 * ```ts
 * const searchApi = debounceAsync(async (query: string) => {
 *   const response = await fetch(`/api/search?q=${query}`);
 *   return response.json();
 * }, 300);
 * 
 * const results = await searchApi('hello');
 * ```
 */
export function debounceAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  wait: number
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeout: NodeJS.Timeout | null = null;
  let resolveList: Array<(value: any) => void> = [];
  let rejectList: Array<(reason: any) => void> = [];

  return function debouncedAsync(...args: Parameters<T>): Promise<ReturnType<T>> {
    if (timeout) {
      clearTimeout(timeout);
    }

    return new Promise((resolve, reject) => {
      resolveList.push(resolve);
      rejectList.push(reject);

      timeout = setTimeout(async () => {
        const currentResolveList = resolveList;
        const currentRejectList = rejectList;
        resolveList = [];
        rejectList = [];

        try {
          const result = await fn(...args);
          currentResolveList.forEach(resolve => resolve(result));
        } catch (error) {
          currentRejectList.forEach(reject => reject(error));
        }
      }, wait);
    });
  };
}

/**
 * Throttle a function - ensures function is called at most once per time period
 * 
 * @param fn - Function to throttle
 * @param limit - Minimum time between calls in milliseconds
 * @returns Throttled function
 * 
 * @example
 * ```ts
 * const trackScroll = throttle(() => {
 *   console.log('Scroll position:', window.scrollY);
 * }, 1000);
 * 
 * window.addEventListener('scroll', trackScroll);
 * // Will fire at most once per second
 * ```
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  let lastArgs: Parameters<T> | null = null;

  return function throttled(...args: Parameters<T>) {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;

      setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          throttled(...lastArgs);
          lastArgs = null;
        }
      }, limit);
    } else {
      lastArgs = args;
    }
  };
}

/**
 * Create a rate-limited wrapper for React Query mutations
 * 
 * @param config - Rate limiter configuration
 * @returns Wrapper function for mutation functions
 * 
 * @example
 * ```ts
 * const rateLimitedMutation = createMutationRateLimiter({
 *   maxRequests: 5,
 *   windowMs: 10000 // 5 requests per 10 seconds
 * });
 * 
 * const mutation = useMutation({
 *   mutationFn: rateLimitedMutation(async (data) => {
 *     return api.updateProfile(data);
 *   })
 * });
 * ```
 */
export function createMutationRateLimiter(config: RateLimiterConfig) {
  const limiter = createRateLimiter(config);

  return function wrapMutation<T extends (...args: any[]) => Promise<any>>(fn: T) {
    return async (...args: Parameters<T>): Promise<ReturnType<T>> => {
      return limiter.attempt(() => fn(...args));
    };
  };
}

/**
 * Pre-configured rate limiters for common use cases
 */
export const RateLimiters = {
  /** For search/autocomplete - 10 requests per 10 seconds */
  search: createRateLimiter({
    maxRequests: 10,
    windowMs: 10000,
    id: 'search'
  }),

  /** For login attempts - 5 attempts per minute */
  auth: createRateLimiter({
    maxRequests: 5,
    windowMs: 60000,
    id: 'auth'
  }),

  /** For file uploads - 5 uploads per minute */
  fileUpload: createRateLimiter({
    maxRequests: 5,
    windowMs: 60000,
    id: 'file-upload'
  }),

  /** For general API calls - 30 requests per minute */
  api: createRateLimiter({
    maxRequests: 30,
    windowMs: 60000,
    id: 'api'
  })
};
